import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'
import webConfig from '~/config'

import pinia from '~/plugins/pinia'

export function initSentry(app: App<Element>) {
  Sentry.init({
    app,
    dsn: 'https://9b75d0a97b23b5de3f9868c9fcaef0fa@o4508087536582656.ingest.us.sentry.io/4508128527384576',
    integrations: [],
    maxBreadcrumbs: 50,
    environment: webConfig.app.envName === 'develop' ? 'development' : webConfig.app.envName,
    release: import.meta.env.VITE_BUILD_ID || 'local',

    // Sample all errors in production, otherwise 70% of errors will be sampled
    sampleRate: webConfig.app.envName.startsWith('production') ? 1.0 : 0.6,
    // Reduce tracing in production to reduce the amount of data sent to Sentry
    tracesSampleRate: webConfig.app.envName.startsWith('production') ? 0.5 : 0.8,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api(?:\.(?:dev|sandbox))?\.oliveltd\.co(?:m|\.uk)\//,
    ],
    // Session Replay
    replaysSessionSampleRate: webConfig.app.envName.startsWith('production') ? 0.6 : 0.2, // 60% in prod, 20% in sandbox (off in dev)
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // ⛔️ Ignore specific error messages
    ignoreErrors: [
      'Login required',
      'missing_refresh_token',
      'Failed to fetch dynamically imported module',
    ],
  })
}

export function configureSentry(app: App<Element>, router: Router) {
  pinia.use(Sentry.createSentryPiniaPlugin())

  Sentry.addIntegration(Sentry.vueIntegration({
    tracingOptions: {
      trackComponents: true,
      hooks: [
        'mount',
        'update',
      ],
    },
  }))

  Sentry.addIntegration(Sentry.browserTracingIntegration({ router }))
  Sentry.addIntegration(Sentry.httpClientIntegration())

  if (webConfig.app.envName.startsWith('production')) {
    Sentry.addIntegration(Sentry.replayIntegration())
  }
}
